/**
 * /erp index — overview landing for the ERP module (Manager+).
 *
 * v1 PR-A: a small dashboard of "what's here, what's coming". As more
 * modules ship (inventory PR-B, dispatch PR-F, etc.) this page will
 * grow into a real overview with KPIs / recent activity. For PR-A it's
 * just navigation cards so a manager opening the ERP for the first
 * time isn't dropped on an empty Outlet.
 */

import { Link, createFileRoute } from "@tanstack/react-router";
import {
  Box,
  Boxes,
  Factory,
  Package,
  Settings as SettingsIcon,
  ShoppingCart,
  Truck,
  Wrench,
} from "lucide-react";
import type { ComponentType, SVGProps } from "react";

interface OverviewCard {
  href: string;
  label: string;
  description: string;
  icon: ComponentType<SVGProps<SVGSVGElement>>;
  /** When true, the card is shown but disabled (coming soon). */
  upcoming?: boolean;
}

const CARDS: OverviewCard[] = [
  {
    href: "/erp/products",
    label: "Products",
    description: "Sellable / manufacturable items.",
    icon: Box,
  },
  {
    href: "/erp/raw-materials",
    label: "Raw Materials",
    description: "Consumable inputs for manufacturing.",
    icon: Wrench,
  },
  {
    href: "/erp/settings",
    label: "Settings",
    description: "Warehouses (and later, drivers + transport).",
    icon: SettingsIcon,
  },
  {
    href: "/erp/inventory",
    label: "Inventory",
    description: "Per-warehouse stock levels and adjustments.",
    icon: Boxes,
  },
  {
    href: "/erp/manufacturing",
    label: "Manufacturing",
    description: "Production jobs that create finished products.",
    icon: Factory,
  },
  {
    href: "/erp/dispatches",
    label: "Dispatch",
    description: "Outbound and inter-warehouse shipments.",
    icon: Truck,
  },
  {
    href: "/erp/orders",
    label: "Orders",
    description: "Customer orders — build them, then dispatch.",
    icon: ShoppingCart,
  },
];

export const Route = createFileRoute("/_app/erp/")({
  component: ErpOverviewPage,
});

function ErpOverviewPage() {
  return (
    <div className="page-shell space-y-5 p-4 sm:p-6">
      <header className="space-y-1">
        <h1 className="flex items-center gap-2 text-2xl font-semibold tracking-tight">
          <Package className="size-5 opacity-70" aria-hidden />
          ERP Overview
        </h1>
        <p className="text-sm opacity-60">
          Catalog, inventory, and dispatch — integrated with the CRM. Use the launcher to switch
          back at any time.
        </p>
      </header>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {CARDS.map((c) => {
          const Icon = c.icon;
          if (c.upcoming) {
            return (
              <div
                key={c.label}
                className="flex items-start gap-3 rounded-xl border border-dashed border-[var(--border)] p-4 opacity-60"
                aria-disabled="true"
              >
                <Icon className="mt-0.5 size-5 shrink-0 opacity-70" aria-hidden />
                <div className="flex flex-col">
                  <span className="font-medium">
                    {c.label}{" "}
                    <span className="ml-1 inline-block rounded-full bg-black/5 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider dark:bg-white/10">
                      Coming soon
                    </span>
                  </span>
                  <span className="text-xs opacity-70">{c.description}</span>
                </div>
              </div>
            );
          }
          return (
            <Link
              key={c.label}
              to={c.href}
              className="group flex items-start gap-3 rounded-xl border border-[var(--border)] p-4 transition-colors hover:border-[var(--accent)] hover:bg-[var(--accent)]/5"
            >
              <Icon
                className="mt-0.5 size-5 shrink-0 opacity-70 transition-colors group-hover:text-[var(--accent)] group-hover:opacity-100"
                aria-hidden
              />
              <div className="flex flex-col">
                <span className="font-medium">{c.label}</span>
                <span className="text-xs opacity-60">{c.description}</span>
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
}
